import React, { useMemo, useState } from "react";
import EditorialCtaFlow from "./EditorialCtaFlow";
import HeroSettingsPanel from "./HeroSettingsPanel";
import StatGroup from "./StatGroup";

const TEXT_WIDTHS = ["small", "medium", "large"];
const CONTENT_WIDTHS = ["60%", "70%", "75%", "80%", "100%"];
const MEDIA_WIDTHS = ["narrow", "default", "wide"];
const MEDIA_SIDES = ["left", "right"];
const SPACE_VALUES = ["none", "xs", "small", "medium", "large"];
const MEDIA_INSETS = ["none", "small", "medium"];
const MEDIA_FITS = ["cover", "contain"];
const MEDIA_POSITIONS = ["center", "top", "bottom", "left", "right", "left-top", "right-top", "left-bottom", "right-bottom"];
const TREATMENT_BACKGROUNDS = ["default", "soft-blue", "none"];
const TREATMENT_BLENDS = ["none", "subtle", "standard", "strong"];
const TREATMENT_WIDTHS = ["narrow", "default", "wide"];
const BOOLEAN_PARAMETERS = ["mediaBleedRight", "mediaBleedBottom", "showRegions"];
const HERO_PARAMETERS = {
  textWidth: TEXT_WIDTHS,
  contentWidth: CONTENT_WIDTHS,
  mediaWidth: MEDIA_WIDTHS,
  mediaSide: MEDIA_SIDES,
  mediaTopGap: SPACE_VALUES,
  mediaInset: MEDIA_INSETS,
  mediaMarginTop: SPACE_VALUES,
  mediaMarginRight: SPACE_VALUES,
  mediaMarginBottom: SPACE_VALUES,
  mediaMarginLeft: SPACE_VALUES,
  mediaFit: MEDIA_FITS,
  mediaPosition: MEDIA_POSITIONS,
  mediaBackground: TREATMENT_BACKGROUNDS,
  leftBlend: TREATMENT_BLENDS,
  leftBlendWidth: TREATMENT_WIDTHS,
  topBlend: TREATMENT_BLENDS,
  topBlendHeight: TREATMENT_WIDTHS,
};
const LEGACY_HERO_PARAMETERS = ["blend", "blendWidth"];

/**
 * @typedef {Object} EditorialHeroMedia
 * @property {string} src Required image source.
 * @property {string} [srcSet] Responsive image candidates.
 * @property {string} [sizes] Responsive image sizing hint.
 * @property {string} [alt=""] Image alternative text.
 * @property {number|string} [width] Intrinsic image width.
 * @property {number|string} [height] Intrinsic image height.
 * @property {"eager"|"lazy"} [loading] Native image loading behavior.
 * @property {"sync"|"async"|"auto"} [decoding] Native image decoding behavior.
 * @property {"high"|"low"|"auto"} [fetchPriority] Image fetch priority.
 * @property {string} [objectPosition] Focal point inside the image crop, such as "58% 42%". Unlike mediaOffsetX/Y, this changes which part of the image remains visible rather than moving the integrated media layer.
 */

/**
 * @typedef {Object} EditorialHeroMediaTreatment
 * @property {"default"|"soft-blue"|"none"} [background="default"] Media background treatment.
 * @property {"none"|"subtle"|"standard"|"strong"} [leftBlend="standard"] Left blend strength.
 * @property {"narrow"|"default"|"wide"} [leftBlendWidth="default"] Left blend width.
 * @property {"none"|"subtle"|"standard"|"strong"} [topBlend="none"] Top blend strength.
 * @property {"narrow"|"default"|"wide"} [topBlendHeight="default"] Top blend height.
 */

/**
 * @typedef {Object} EditorialHeroProps
 * @property {React.ReactNode} [eyebrow] Optional eyebrow; omitted values render no eyebrow markup.
 * @property {React.ReactNode} [title] Optional title; omitted values render no heading markup.
 * @property {"h1"|"h2"|"h3"} [headingTag="h1"] Semantic element used when title is present.
 * @property {React.ReactNode|React.ReactNode[]} [supporting] Optional supporting copy; omitted values render no supporting wrapper.
 * @property {React.ReactNode|React.ReactNode[]} [description] Backward-compatible alias for supporting.
 * @property {number|string} [minHeight] Optional minimum height. Numbers resolve to pixels; CSS lengths are passed through. The default is content-driven height.
 * @property {Array<Object>} [stats=[]] Optional statistics rendered below supporting copy.
 * @property {React.ReactNode} [actions] Optional action content.
 * @property {EditorialHeroMedia|React.ReactNode} [media] Optional integrated media.
 * @property {"small"|"medium"|"large"} [textWidth="medium"] Text-column width preset.
 * @property {"60%"|"70%"|"75%"|"80%"|"100%"} [contentWidth="75%"] Maximum width used by text-only heroes.
 * @property {"narrow"|"default"|"wide"} [mediaWidth="default"] Media-column width preset.
 * @property {"left"|"right"} [mediaSide="right"] Integrated-media side.
 * @property {"none"|"xs"|"small"|"medium"|"large"} [mediaTopGap] Optional media top-gap preset.
 * @property {boolean} [mediaBleedRight=true] Whether media may bleed to the hero's right edge.
 * @property {boolean} [mediaBleedBottom=true] Whether media may bleed to the hero's bottom edge.
 * @property {{top?: string, right?: string, bottom?: string, left?: string}} [mediaMargins={}] Optional approved media margin presets by side.
 * @property {"none"|"small"|"medium"} [mediaInset="none"] Media inset preset.
 * @property {"cover"|"contain"} [mediaFit="contain"] Image fitting behavior; cover activates the shared integrated-photo treatment.
 * @property {"center"|"top"|"bottom"|"left"|"right"|"left-top"|"right-top"|"left-bottom"|"right-bottom"} [mediaPosition="right-bottom"] Focal-position preset used when media.objectPosition is not more specific.
 * @property {number|string} [mediaOffsetX=0] Horizontal integrated-media adjustment; negative moves left and positive moves right. This moves the media layer, not its focal point.
 * @property {number|string} [mediaOffsetY=0] Vertical integrated-media adjustment; negative moves up and positive moves down. This moves the media layer, not its focal point.
 * @property {EditorialHeroMediaTreatment} [mediaTreatment={}] Background and blend treatment.
 * @property {boolean} [enableSettings=true] Enables the development-only query-string settings panel.
 * @property {React.ReactNode} [children] Optional hero footer content.
 */

/**
 * Canonical editorial page hero. Height is content-driven unless minHeight is supplied.
 *
 * @example <EditorialHero eyebrow="About" title="A clear title" supporting="Supporting copy." />
 * @example <EditorialHero eyebrow="Work" supporting="A hero intentionally without a title." />
 * @example <EditorialHero title="Content-driven by default" supporting="No minHeight is required." />
 * @example <EditorialHero title="Explicit minimum" minHeight="28rem" />
 * @example <EditorialHero title="Adjusted media" media={media} mediaOffsetY={-24} />
 * @param {EditorialHeroProps} props
 */

const normalize = (value, validValues, fallback, propName) => {
  if (validValues.includes(value)) return value;

  if (process.env.NODE_ENV !== "production") {
    console.warn(
      `EditorialHero: invalid ${propName} "${value}". Using "${fallback}".`,
    );
  }

  return fallback;
};

const renderDescription = (description) => {
  const items = Array.isArray(description) ? description : [description];

  return items.map((item, index) =>
    typeof item === "string" ? <p key={index}>{item}</p> : <React.Fragment key={index}>{item}</React.Fragment>,
  );
};

const resolveCssLength = (value, fallback) => {
  if (typeof value === "number" && Number.isFinite(value)) return `${value}px`;
  if (typeof value === "string" && value.trim()) return value.trim();
  return fallback;
};

const EditorialHero = ({
  eyebrow,
  title,
  headingTag = "h1",
  supporting,
  description,
  stats = [],
  actions,
  media,
  textWidth = "medium",
  contentWidth = "75%",
  mediaWidth = "default",
  mediaSide = "right",
  mediaTopGap,
  mediaBleedRight = true,
  mediaBleedBottom = true,
  mediaMargins = {},
  mediaInset = "none",
  mediaFit = "contain",
  mediaPosition = "right-bottom",
  mediaOffsetX = 0,
  mediaOffsetY = 0,
  mediaTreatment = {},
  minHeight,
  enableSettings = true,
  children,
}) => {
  const committedSettings = useMemo(() => ({
    textWidth,
    contentWidth,
    mediaWidth,
    mediaSide,
    mediaBleedRight,
    mediaBleedBottom,
    mediaInset,
    mediaTopGap: mediaTopGap || "none",
    mediaMarginTop: mediaMargins.top || "none",
    mediaMarginRight: mediaMargins.right || "none",
    mediaMarginBottom: mediaMargins.bottom || "none",
    mediaMarginLeft: mediaMargins.left || "none",
    mediaFit,
    mediaPosition,
    mediaBackground: mediaTreatment.background || "default",
    leftBlend: mediaTreatment.leftBlend || mediaTreatment.blend || "standard",
    leftBlendWidth: mediaTreatment.leftBlendWidth || mediaTreatment.blendWidth || "default",
    topBlend: mediaTreatment.topBlend || "none",
    topBlendHeight: mediaTreatment.topBlendHeight || "default",
    showRegions: false,
  }), [textWidth, contentWidth, mediaWidth, mediaSide, mediaTopGap, mediaBleedRight, mediaBleedBottom, mediaInset, mediaMargins, mediaFit, mediaPosition, mediaTreatment]);
  const settingsActive = process.env.NODE_ENV !== "production" && enableSettings && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("heroSettings") === "1";
  const [settingsVisible, setSettingsVisible] = useState(settingsActive);
  const [previewSettings, setPreviewSettings] = useState(() => {
    if (!settingsActive) return committedSettings;
    const parameters = new URLSearchParams(window.location.search);
    const parsed = { ...committedSettings };
    Object.entries(HERO_PARAMETERS).forEach(([name, values]) => {
      if (values.includes(parameters.get(name))) parsed[name] = parameters.get(name);
    });
    if (!parameters.has("leftBlend") && TREATMENT_BLENDS.includes(parameters.get("blend"))) parsed.leftBlend = parameters.get("blend");
    if (!parameters.has("leftBlendWidth") && TREATMENT_WIDTHS.includes(parameters.get("blendWidth"))) parsed.leftBlendWidth = parameters.get("blendWidth");
    BOOLEAN_PARAMETERS.forEach((name) => {
      if (["0", "1"].includes(parameters.get(name))) parsed[name] = parameters.get(name) === "1";
    });
    return parsed;
  });
  const previewEnabled = settingsActive && settingsVisible;
  const activeSettings = previewEnabled ? previewSettings : committedSettings;
  const hasMedia = Boolean(media);
  const Heading = ["h1", "h2", "h3"].includes(headingTag) ? headingTag : "h1";
  const resolvedSupporting = supporting !== undefined ? supporting : description;
  const hasSupporting = Array.isArray(resolvedSupporting)
    ? resolvedSupporting.length > 0
    : Boolean(resolvedSupporting);
  const heroStyle = {
    "--editorial-hero-media-offset-x": resolveCssLength(mediaOffsetX, "0px"),
    "--editorial-hero-media-offset-y": resolveCssLength(mediaOffsetY, "0px"),
    ...(minHeight !== undefined && {
      "--editorial-hero-min-height": resolveCssLength(minHeight, "auto"),
    }),
  };
  const resolvedTextWidth = normalize(activeSettings.textWidth, TEXT_WIDTHS, "medium", "textWidth");
  const resolvedContentWidth = normalize(activeSettings.contentWidth, CONTENT_WIDTHS, "75%", "contentWidth");
  const resolvedMediaWidth = normalize(activeSettings.mediaWidth, MEDIA_WIDTHS, "default", "mediaWidth");
  const resolvedMediaSide = normalize(activeSettings.mediaSide, MEDIA_SIDES, "right", "mediaSide");
  const resolvedTopGap = normalize(activeSettings.mediaTopGap, SPACE_VALUES, "none", "mediaTopGap");
  const resolvedInset = normalize(activeSettings.mediaInset, MEDIA_INSETS, "none", "mediaInset");
  const resolvedFit = normalize(activeSettings.mediaFit, MEDIA_FITS, "contain", "mediaFit");
  const resolvedPosition = normalize(activeSettings.mediaPosition, MEDIA_POSITIONS, "right-bottom", "mediaPosition");
  const background = normalize(activeSettings.mediaBackground, TREATMENT_BACKGROUNDS, "default", "mediaTreatment.background");
  const leftBlend = normalize(activeSettings.leftBlend, TREATMENT_BLENDS, "standard", "mediaTreatment.leftBlend");
  const leftBlendWidth = normalize(activeSettings.leftBlendWidth, TREATMENT_WIDTHS, "default", "mediaTreatment.leftBlendWidth");
  const topBlend = normalize(activeSettings.topBlend, TREATMENT_BLENDS, "none", "mediaTreatment.topBlend");
  const topBlendHeight = normalize(activeSettings.topBlendHeight, TREATMENT_WIDTHS, "default", "mediaTreatment.topBlendHeight");
  const margins = ["top", "right", "bottom", "left"].reduce((result, side) => {
    result[side] = normalize(activeSettings[`mediaMargin${side[0].toUpperCase()}${side.slice(1)}`], SPACE_VALUES, "none", `mediaMargins.${side}`);
    return result;
  }, {});
  const mediaSource = typeof media === "object" && !React.isValidElement(media) ? media : null;
  const classes = [
    "editorial-hero",
    hasMedia ? "editorial-hero--with-media" : "editorial-hero--without-media",
    `editorial-hero--text-${resolvedTextWidth}`,
    `editorial-hero--media-${resolvedMediaWidth}`,
    `editorial-hero--media-side-${resolvedMediaSide}`,
    (mediaTopGap !== undefined || previewEnabled) && `editorial-hero--top-gap-${resolvedTopGap}`,
    `editorial-hero--content-${resolvedContentWidth.replace("%", "")}`,
    `editorial-hero--inset-${resolvedInset}`,
    `editorial-hero--fit-${resolvedFit}`,
    `editorial-hero--position-${resolvedPosition}`,
    `editorial-hero--background-${background}`,
    `editorial-hero--left-blend-${leftBlend}`,
    `editorial-hero--left-blend-width-${leftBlendWidth}`,
    `editorial-hero--top-blend-${topBlend}`,
    `editorial-hero--top-blend-height-${topBlendHeight}`,
    activeSettings.mediaBleedRight && "editorial-hero--bleed-right",
    activeSettings.mediaBleedBottom && "editorial-hero--bleed-bottom",
    previewEnabled && activeSettings.showRegions && "editorial-hero--show-regions",
    ...Object.entries(margins).map(([side, value]) => `editorial-hero--margin-${side}-${value}`),
  ].filter(Boolean).join(" ");

  const updatePreview = (name, value) => {
    const next = { ...previewSettings, [name]: value };
    setPreviewSettings(next);
    const parameters = new URLSearchParams(window.location.search);
    parameters.set("heroSettings", "1");
    Object.keys(HERO_PARAMETERS).forEach((key) => parameters.set(key, next[key]));
    BOOLEAN_PARAMETERS.forEach((key) => parameters.set(key, next[key] ? "1" : "0"));
    window.history.replaceState(null, "", `${window.location.pathname}?${parameters.toString()}${window.location.hash}`);
  };
  const removePreviewParameters = (dismiss = false) => {
    const parameters = new URLSearchParams(window.location.search);
    [...Object.keys(HERO_PARAMETERS), ...LEGACY_HERO_PARAMETERS, ...BOOLEAN_PARAMETERS].forEach((key) => parameters.delete(key));
    if (dismiss) parameters.delete("heroSettings");
    const query = parameters.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    setPreviewSettings(committedSettings);
    if (dismiss) setSettingsVisible(false);
  };
  const copyConfiguration = () => navigator.clipboard.writeText(JSON.stringify({
    textWidth: activeSettings.textWidth,
    contentWidth: activeSettings.contentWidth,
    mediaWidth: activeSettings.mediaWidth,
    mediaSide: activeSettings.mediaSide,
    mediaTopGap: activeSettings.mediaTopGap,
    mediaBleedRight: activeSettings.mediaBleedRight,
    mediaBleedBottom: activeSettings.mediaBleedBottom,
    mediaInset: activeSettings.mediaInset,
    mediaFit: activeSettings.mediaFit,
    mediaPosition: activeSettings.mediaPosition,
    mediaMargins: { top: activeSettings.mediaMarginTop, right: activeSettings.mediaMarginRight, bottom: activeSettings.mediaMarginBottom, left: activeSettings.mediaMarginLeft },
    mediaTreatment: { background: activeSettings.mediaBackground, leftBlend: activeSettings.leftBlend, leftBlendWidth: activeSettings.leftBlendWidth, topBlend: activeSettings.topBlend, topBlendHeight: activeSettings.topBlendHeight },
  }, null, 2));

  return (
    <header className={classes} style={heroStyle}>
      <div className="container editorial-hero__container">
        <div className="editorial-hero__content">
          {eyebrow && <p className="editorial-hero__eyebrow">{eyebrow}</p>}
          {title && <Heading className="editorial-hero__title">{title}</Heading>}
          {hasSupporting && actions ? (
            <div className="editorial-hero__description">
              <EditorialCtaFlow
                action={actions}
                actionClassName="editorial-hero__actions"
              >
                {renderDescription(resolvedSupporting)}
              </EditorialCtaFlow>
            </div>
          ) : hasSupporting ? (
            <div className="editorial-hero__description">
              {renderDescription(resolvedSupporting)}
            </div>
          ) : null}
          {!hasSupporting && actions && (
            <div className="editorial-hero__actions">{actions}</div>
          )}
          {stats.length > 0 && <StatGroup stats={stats} />}
        </div>

        {hasMedia && (
          <div className="editorial-hero__media">
            <span className="editorial-hero__media-background" aria-hidden="true" />
            <span className="editorial-hero__blend-left" aria-hidden="true" />
            <span className="editorial-hero__blend-top" aria-hidden="true" />
            <div className="editorial-hero__media-frame">
              {mediaSource ? (
                <img
                  alt={mediaSource.alt || ""}
                  decoding={mediaSource.decoding}
                  fetchPriority={mediaSource.fetchPriority}
                  height={mediaSource.height}
                  loading={mediaSource.loading}
                  sizes={mediaSource.sizes}
                  src={mediaSource.src}
                  srcSet={mediaSource.srcSet}
                  style={
                    mediaSource.objectPosition
                      ? { objectPosition: mediaSource.objectPosition }
                      : undefined
                  }
                  width={mediaSource.width}
                />
              ) : (
                media
              )}
            </div>
          </div>
        )}
      </div>
      {children && <div className="container editorial-hero__footer">{children}</div>}
      {previewEnabled && <HeroSettingsPanel settings={activeSettings} onChange={updatePreview} onReset={() => removePreviewParameters(false)} onDismiss={() => removePreviewParameters(true)} onCopyUrl={() => navigator.clipboard.writeText(window.location.href)} onCopyConfiguration={copyConfiguration} />}
    </header>
  );
};

export default EditorialHero;
